import { Pagination } from "@bfc-software/pallet";
// eslint-disable-next-line no-restricted-imports -- follow-up: use useCurrentLocalization()
import { TFunction } from "i18next";
import { isDefined } from "@bfc-software/app-toolbox";
import { TaskPlannerSchemaTypes } from "@bfc-software/api-schemas";
import { type PageToken } from "@bfc-software/bfc-web-ui/lib/cursor-pagination";

import { CursorPager } from "./cursor-pager";

type PaginationState =
  TaskPlannerSchemaTypes.components["schemas"]["Pagination"];

/**
 * The pager for a cursor-paginated list, in whichever of two shapes the
 * response can support.
 *
 * Every field of `Pagination` but `has_more` is optional, and absence means
 * "this listing does not report that" -- never a zero or a false. So the
 * response itself decides which pager the route gets:
 *
 *   - counting listings (`page_number`/`page_count` present) get pallet's
 *     `Pagination`, with a real "3 / 12" readout and a Previous that works.
 *   - everything else falls back to `CursorPager`, forward-only, no readout.
 *
 * The fallback is not dead code waiting to be deleted: the endpoints behind the
 * load plan history and the plan activations list still answer forward-only,
 * and each moves to the counted shape on its own schedule with no change needed
 * here.
 *
 * `jumpTo` is deliberately never passed. It would render a page-number select,
 * and keyset ordinals are not addressable -- there is no token for "page 7", so
 * the control could not do what it appears to offer.
 */
export function ListPager({
  pagination,
  isFetching,
  onChange,
  t,
}: {
  pagination?: PaginationState;
  isFetching: boolean;
  onChange: (page: PageToken) => void;
  t: TFunction;
}) {
  // Read the tokens rather than the flags. `cursor` is documented NULL whenever
  // has_more is false and `prev_cursor` NULL whenever has_prev is false, so
  // going by the token means a response that disagrees with itself can never
  // produce a button that navigates nowhere.
  const nextCursor = pagination?.has_more === true ? pagination.cursor : null;
  const prevCursor =
    pagination?.has_prev === true ? pagination.prev_cursor : null;

  const { page_number: pageNumber, page_count: pageCount } = pagination ?? {};

  if (!isDefined(pageNumber) || !isDefined(pageCount)) {
    return (
      <CursorPager
        pagination={pagination}
        isFetching={isFetching}
        onChange={(cursor) => onChange({ cursor, dir: "next" })}
        t={t}
      />
    );
  }

  return (
    <Pagination
      data-testid="list-pager"
      // Straight from the server. Both are 0 on an empty page, which reads as
      // "0 / 0" -- the honest rendering of a listing with nothing in it.
      currentPage={pageNumber}
      totalPages={pageCount}
      previousButton={{
        isDisabled: !isDefined(prevCursor) || isFetching,
        onPress: () =>
          isDefined(prevCursor) &&
          onChange({ cursor: prevCursor, dir: "prev" }),
      }}
      nextButton={{
        isDisabled: !isDefined(nextCursor) || isFetching,
        onPress: () =>
          isDefined(nextCursor) &&
          onChange({ cursor: nextCursor, dir: "next" }),
      }}
    />
  );
}
